import React, { useState, useEffect, useRef } from 'react';
import '../Stylesheets/InlineLeadForm.css';
import AnimatedCheckmark from './AnimatedCheckmark';
import TypewriterCycle from '../Utils/Typewriter';
import Plaque from './Plaque';
import Badge from './Badge';
import smartwaiverLogo from '../Media/smartwaiver_logo.webp';

export default function InlineLeadForm() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

const [status, setStatus] = useState('idle');
  const [showInstructions, setShowInstructions] = useState(false);
  const [fadeOutCheck, setFadeOutCheck] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const plaqueRef = useRef(null);
  const [lockedHeight, setLockedHeight] = useState(null);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      let cleaned = value.replace(/\D/g, '');
      if (cleaned.length === 11 && cleaned.startsWith('1')) cleaned = cleaned.slice(1);
      let formatted = cleaned;
      if (cleaned.length > 3 && cleaned.length <= 6) {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      } else if (cleaned.length > 6) {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      }
      setFormData((p) => ({ ...p, phone: formatted }));
    } else {
      setFormData((p) => ({ ...p, [name]: value }));
    }
  };

  const cleanedPhone = formData.phone.replace(/\D/g, '');
  const isValid =
    formData.firstName.trim() !== '' &&
    formData.lastName.trim() !== '' &&
    validateEmail(formData.email) &&
    cleanedPhone.length === 10;

  useEffect(() => {
    if (status !== 'success') return;
    setShowInstructions(false);
    setFadeOutCheck(false);
    const fadeTimer = setTimeout(() => setFadeOutCheck(true), 1200);
    const swapTimer = setTimeout(() => setShowInstructions(true), 1900);
    return () => { clearTimeout(fadeTimer); clearTimeout(swapTimer); };
  }, [status]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || status === 'submitting') return;
    if (plaqueRef.current) setLockedHeight(plaqueRef.current.offsetHeight);
    setStatus('submitting');
    setErrorMsg('');
    try {
      const response = await fetch(
        'https://worker-consolidated.maxli5004.workers.dev/intake-form',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-maple': 'false' },
          mode: 'cors',
          body: JSON.stringify({ ...formData, phone: formData.phone.replace(/\D/g, '') }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setErrorMsg(err?.message || 'Unexpected error');
        setStatus('error');
        return;
      }
      await response.json().catch(() => ({}));
      setStatus('success');
      window.gtag?.('event', 'generate_lead', { event_category: 'form', event_label: 'intake_form' });
      setFormData({ firstName: '', lastName: '', email: '', phone: '' });
    } catch (err) {
      setErrorMsg('Error submitting the form');
      setStatus('error');
    }
  };

  return (
    <Plaque success={status === 'success'} minHeight={lockedHeight} innerRef={plaqueRef}>
      {status === 'success' ? (
        <div className="lf-success-wrap">
          {!showInstructions ? (
            <div className={`check-wrap ${fadeOutCheck ? 'fade-out' : ''}`}>
              <AnimatedCheckmark />
            </div>
          ) : (
            <>
              <p className="lf-success-title">
                <TypewriterCycle speed={45} startDelay={0}>One step left —</TypewriterCycle>
              </p>
              <p className="lf-success-sub slide-in" style={{ '--delay': '0.9s' }}>
                Sign the waiver and then show up to any class!
              </p>
              <a
                className="lf-waiver-btn slide-in"
                style={{ '--delay': '1.5s' }}
                href="https://waiver.smartwaiver.com/w/mhpajduzerdvjhmxcx67qd/web/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img className="lf-waiver-logo" src={smartwaiverLogo} alt="Smartwaiver" />
                <span className="lf-waiver-text">
                  <span className="lf-waiver-cta">Sign the Waiver</span>
                  <span className="lf-waiver-sub">Tap to complete — takes 1 minute</span>
                </span>
                <span className="lf-waiver-arrow">→</span>
              </a>
              <div className="lf-success-logo fade-in-late" style={{ '--delay': '3.0s' }}>
                <span className="nav-brand-main">Richmond Hill</span>
                <span className="nav-brand-rule" aria-hidden="true" />
                <span className="nav-brand-sub">Jiu · Jitsu</span>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="lf-top">
            <Badge>7-Day Free Trial</Badge>
            <p className="lf-sub">No commitment &nbsp;·&nbsp; All levels &nbsp;·&nbsp; Adults &amp; Kids</p>
          </div>

          {status === 'error' && <p className="lf-error">{errorMsg}</p>}

          <form onSubmit={handleSubmit} className="lf-form">
            <div className="lf-row">
              <div className="lf-field">
                <input className="lf-input" id="rh-firstName" type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder=" " required autoComplete="given-name" />
                <label className="lf-label" htmlFor="rh-firstName">First Name</label>
              </div>
              <div className="lf-field">
                <input className="lf-input" id="rh-lastName" type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder=" " required autoComplete="family-name" />
                <label className="lf-label" htmlFor="rh-lastName">Last Name</label>
              </div>
            </div>

            <div className="lf-row">
              <div className="lf-field">
                <input className="lf-input" id="rh-email" type="email" name="email" value={formData.email} onChange={handleChange} placeholder=" " required autoComplete="email" />
                <label className="lf-label" htmlFor="rh-email">Email</label>
              </div>
              <div className="lf-field">
                <input className="lf-input" id="rh-phone" type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder=" " required autoComplete="tel" />
                <label className="lf-label" htmlFor="rh-phone">Phone</label>
              </div>
            </div>

            <button
              type="submit"
              className={`lf-btn ${isValid ? 'lf-btn--active' : 'lf-btn--disabled'}`}
              disabled={!isValid || status === 'submitting'}
            >
              {status === 'submitting' ? 'Submitting…' : 'CLAIM YOUR FREE TRIAL →'}
            </button>
          </form>
        </>
      )}
    </Plaque>
  );
}
